import { useEffect, useState } from 'preact/hooks'

type Status = 'loading' | 'loaded' | 'error'

const RADIUS = 54
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const TICKS = 24

const mb = (n: number): string => (n / 1048576).toFixed(2)

// Radial deco tick marks around the ring (Erte/Atari-gauge geometry).
const ticks = Array.from({ length: TICKS }, (_, i) => {
	const angle = (i * (360 / TICKS) * Math.PI) / 180
	const major = i % 6 === 0
	const inner = 60
	const outer = major ? 67 : 63
	return {
		key: i,
		x1: 60 + inner * Math.cos(angle),
		y1: 60 + inner * Math.sin(angle),
		x2: 60 + outer * Math.cos(angle),
		y2: 60 + outer * Math.sin(angle),
		major,
	}
})

// A large optimized image with a vector-instrument loading state: bytes are streamed
// via fetch so the progress ring is driven by the real download, then the image opens
// with a clean iris reveal. Falls back to a plain <img> load with an indeterminate
// spinning ring if streaming isn't available.
export default function FullsizeImage({ src, alt }: { src: string; alt: string }) {
	const [status, setStatus] = useState<Status>('loading')
	const [received, setReceived] = useState(0)
	const [total, setTotal] = useState(0)
	const [indeterminate, setIndeterminate] = useState(false)
	const [resolvedSrc, setResolvedSrc] = useState<string | null>(null)

	useEffect(() => {
		let cancelled = false
		let objectUrl: string | null = null
		setStatus('loading')
		setReceived(0)
		setTotal(0)
		setIndeterminate(false)
		setResolvedSrc(null)

		const stream = async () => {
			const res = await fetch(src)
			if (!res.ok || !res.body) throw new Error('unstreamable')
			const length = Number(res.headers.get('Content-Length')) || 0
			if (length) setTotal(length)
			else setIndeterminate(true)

			const reader = res.body.getReader()
			const chunks: Uint8Array[] = []
			let count = 0
			for (;;) {
				// eslint-disable-next-line no-await-in-loop
				const { done, value } = await reader.read()
				if (done) break
				if (value) {
					chunks.push(value)
					count += value.length
					if (!cancelled) setReceived(count)
				}
			}
			if (cancelled) return
			objectUrl = URL.createObjectURL(new Blob(chunks as BlobPart[]))
			setResolvedSrc(objectUrl)
		}

		stream().catch(() => {
			// Streaming unavailable (e.g. opaque response) — let the browser load directly.
			if (!cancelled) {
				setIndeterminate(true)
				setResolvedSrc(src)
			}
		})

		return () => {
			cancelled = true
			if (objectUrl) URL.revokeObjectURL(objectUrl)
		}
	}, [src])

	const loaded = status === 'loaded'
	const errored = status === 'error'
	const progress = total ? received / total : 0
	const pct = Math.round(progress * 100)
	const dashoffset = indeterminate ? CIRCUMFERENCE * 0.7 : CIRCUMFERENCE * (1 - progress)

	return (
		<div
			className={`relative overflow-hidden bg-void ${
				loaded ? '' : 'w-[80vw] max-w-2xl aspect-video'
			}`}
		>
			{resolvedSrc && (
				<img
					src={resolvedSrc}
					alt={alt}
					onLoad={() => setStatus('loaded')}
					onError={() => setStatus('error')}
					className={`block max-w-[90vw] max-h-[80vh] object-contain transition-[clip-path,opacity] duration-700 ease-out ${
						loaded
							? 'opacity-100 [clip-path:circle(150%_at_50%_50%)]'
							: 'opacity-0 [clip-path:circle(0%_at_50%_50%)]'
					}`}
				/>
			)}

			{!loaded && (
				<div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-void">
					{/* Vector HUD corner brackets */}
					{[
						'top-4 left-4 border-t border-l',
						'top-4 right-4 border-t border-r',
						'bottom-4 left-4 border-b border-l',
						'bottom-4 right-4 border-b border-r',
					].map((pos) => (
						<span key={pos} className={`absolute h-4 w-4 border-primary opacity-40 ${pos}`} />
					))}

					<div className="relative h-40 w-40">
						<svg viewBox="0 0 120 120" className="h-full w-full">
							{ticks.map((t) => (
								<line
									key={t.key}
									x1={t.x1}
									y1={t.y1}
									x2={t.x2}
									y2={t.y2}
									stroke={errored ? '#cc3366' : '#84db00'}
									strokeWidth={t.major ? 1.2 : 0.6}
									opacity={t.major ? 0.55 : 0.25}
								/>
							))}

							{/* Track */}
							<circle
								cx={60}
								cy={60}
								r={RADIUS}
								fill="none"
								stroke={errored ? 'rgba(204,51,102,0.18)' : 'rgba(132,219,0,0.12)'}
								strokeWidth={2}
							/>

							{!errored && (
								<>
									{/* Progress arc — driven by real bytes received */}
									<circle
										cx={60}
										cy={60}
										r={RADIUS}
										fill="none"
										stroke="#84db00"
										strokeWidth={2.5}
										strokeLinecap="butt"
										strokeDasharray={CIRCUMFERENCE}
										strokeDashoffset={dashoffset}
										transform={indeterminate ? undefined : 'rotate(-90 60 60)'}
										className={`drop-shadow-glow-green ${indeterminate ? 'vector-spin' : ''}`}
										style={{
											transition: indeterminate ? undefined : 'stroke-dashoffset 150ms linear',
										}}
									/>

									{/* Magenta leading-edge indicator (determinate only) */}
									{!indeterminate && (
										<g
											transform={`rotate(${progress * 360 - 90} 60 60)`}
											style={{ transition: 'transform 150ms linear' }}
										>
											<circle
												cx={60 + RADIUS}
												cy={60}
												r={2.6}
												fill="#cc3366"
												className="drop-shadow-glow-magenta"
											/>
										</g>
									)}
								</>
							)}
						</svg>

						{/* Center: bold Bauhaus numeral, or a deco diamond on error */}
						<div className="absolute inset-0 flex items-center justify-center">
							{errored ? (
								<span className="vector-pulse h-9 w-9 rotate-45 border border-secondary shadow-glow-magenta" />
							) : (
								!indeterminate && (
									<span
										className="font-black leading-none text-[2.6rem] text-paper"
										style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.04em' }}
									>
										{pct}
										<span className="ml-0.5 align-top text-base font-normal text-primary">%</span>
									</span>
								)
							)}
						</div>
					</div>

					{/* Bauhaus typographic block: rule, label, honest data readout */}
					<div className="flex flex-col items-center gap-2">
						<span className="h-px w-10 bg-primary/[0.45]" />
						<p className="m-0 pl-[0.5em] text-[0.6rem] font-light tracking-[0.5em] text-primary opacity-70 [font-family:monospace]">
							{errored ? 'TRANSMISSION LOST' : indeterminate ? 'BUFFERING' : 'LOADING'}
						</p>
						{!errored && (
							<p className="m-0 text-[0.65rem] tracking-[0.25em] text-paper/50 [font-family:monospace]">
								{total ? `${mb(received)} / ${mb(total)} MB` : `${mb(received)} MB`}
							</p>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
